import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Svg } from "./Svg";
import Slider from "react-slick";
import Modal from "react-modal";
import { Carousel as Lightbox } from "react-responsive-carousel";
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import { setCurrentIndex, show } from "../Actions/LightboxImages.action";
import { useQueryParam } from "../useHooks/useQueryParam";

const CustomArrow = ({ onClick, direction = 'left', extraClass = '' }) => {
    return (
        <button className={`slick-arrow ${extraClass}`} type="button" onClick={onClick}>
            <Svg icon="chevron" extraClass={`chevron-${direction}`} />
        </button>
    )
}

const customStyles = {
    content: {
        background: 'none',
        padding: 0,
        border: 'none',
    },
    overlay: {
        background: 'rgba(0, 0, 0, 0.88)',
        zIndex: 2003,
    },
};

const lightboxSettings = {
    showStatus: false,
    showIndicators: false,
    useKeyboardArrows: true,
    thumbWidth: 50,
    autoPlay: false,
    autoFocus: true,
};

const renderThumbnails = (children) => {
    const images = children.map((item, index) => {
        if (!item || !item.props || !item.props.children) {
            return;
        }

        // find img container
        const imageElementContainer = item.props.children.find(
            (ele) => ele.props.className === 'light-box__image-container'
        );

        // find img to get source
        const imageElement = imageElementContainer.props.children.find(
            (ele) => ele.type === 'img'
        );

        if (!imageElement || !imageElement.props || !imageElement.props.src) {
            return;
        }

        const url = imageElement.props.thumbnailsrcurl || imageElement.props.src;

        return (
            <div
                key={index}
                style={{
                    backgroundImage: 'url(' + url + ')',
                }}
                className="thumbnail__image"
            ></div>
        );
    });

    return images;
};

const ProductImages = ({ images }) => {
    const dispatch = useDispatch();
    const { visible, index } = useSelector((state) => state.lightboxImages);
    const onClose = useCallback(() => dispatch(show(false)), [dispatch]);
    const onClickThumbnail = (index) => {
        dispatch(show(true));
        dispatch(setCurrentIndex(index));
    };

    const [imagesToShow, setImagesToShow] = useState(images);

    const mainImageRef = useRef(null);
    const sliderNavRef = useRef(null);
    const searchTerm = useQueryParam('selectedVariant');

    let sliderNav = {
        slidesToShow: 4,
        slidesToScroll: 1,
        asNavFor: mainImageRef.current,
        dots: false,
        rows: 1,
        centerMode: false,
        focusOnSelect: true,
        vertical: true,
        swipe: true,
        arrows: true,
        infinite: imagesToShow.length >= 4,
        prevArrow: <CustomArrow extraClass="slick-prev" direction="up" />,
        nextArrow: <CustomArrow extraClass="slick-next" direction="down" />
    };

    let mainImageConf = {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        asNavFor: sliderNavRef.current,
        prevArrow: <CustomArrow extraClass="slick-prev" />,
        nextArrow: <CustomArrow extraClass="slick-next" direction="right" />,
        responsive: [
            {
                breakpoint: 1240,
                settings: {
                    arrows: true
                }
            }
        ]
    };

    useEffect(() => {
        if (searchTerm && searchTerm.length !== 0) {
            /**
             * @type {ProductItem}
             */
            const selectedVariant = window.__litium.variants.find(x => x.id === searchTerm);

            if (selectedVariant) {
                const tempImages = selectedVariant.images.map(x => {
                    const tempContainer = document.createElement('div');

                    const imgContainer = document.createElement('div');
                    imgContainer.classList.add('product-images__item');

                    const imgEl = document.createElement('img');
                    imgEl.src = x.fullSrcUrl;
                    imgEl.setAttribute('itemprop', 'image')
                    imgEl.setAttribute('class', 'product-detail__image--alter');


                    imgContainer.appendChild(imgEl);
                    tempContainer.appendChild(imgContainer);

                    return {
                        src: x.fullSrcUrl,
                        thumbnailSrcUrl: x.thumbnailSrcUrl,
                        extraDescription: [x?.extraDescription, selectedVariant.name].join(' - '),
                        html: tempContainer.innerHTML // Used for the non-lightbox gallery, should be refactored.
                    };
                });

                setImagesToShow(tempImages);
            }
        } else {
            setImagesToShow(images);
        }
    }, [searchTerm]);

    return (<>
        <div className="product-images__nav">
            <Slider {...sliderNav} ref={sliderNavRef}>
                {imagesToShow.map((value, index) => (
                    <div
                        className='slick-slide__inner'
                        data-index={index}
                        key={`figure${index}`}
                        onClick={() => {
                            onClickThumbnail(index)
                        }}
                        dangerouslySetInnerHTML={{
                            __html: value.html,
                        }}
                    ></div>
                ))}
            </Slider>
        </div>

        <div className="product-images__main">
            <Slider {...mainImageConf} ref={mainImageRef}>
                {imagesToShow.map((value, index) => (
                    <div
                        className='slick-slide__inner'
                        key={`figure${index}`}
                        data-index={index}
                        onClick={() => {
                            onClickThumbnail(index)
                        }}
                        dangerouslySetInnerHTML={{
                            __html: value.html,
                        }}
                    ></div>
                ))}
            </Slider>
        </div>

        <Modal
            ariaHideApp={false}
            preventScroll={true}
            isOpen={visible}
            onRequestClose={onClose}
            style={customStyles}
            shouldCloseOnOverlayClick={false}
        >
            <Lightbox
                {...lightboxSettings}
                selectedItem={index}
                className="light-box"
                renderThumbs={renderThumbnails}
            >
                {imagesToShow.map((img, index) => (
                    <div
                        key={`figure${index}`}
                        className="light-box__container"
                    >
                        <button
                            onClick={onClose}
                            className="light-box__close-btn"
                        >
                            &times;
                        </button>
                        <div className="light-box__image-container">
                            <img src={img.src} thumbnailsrcurl={img?.thumbnailSrcUrl || img.src || null} className="light-box__image" alt={img?.extraDescription || null}/>
                            {img.extraDescription &&
                                <span className="light-box__image-description" title={img.extraDescription}>
                                    {img.extraDescription}
                                </span>
                            }
                        </div>
                    </div>
                ))}
            </Lightbox>
        </Modal>
    </>);
}

export default ProductImages;
